import type { Rates } from '@/rules/labour';
/**
 * Per-tenant values the web app kept in this browser under `timefly.<key>.<tenantId>` until
 * 2026-09-25: the need profile per weekday and hour, the sales per day, the wage target, the rates.
 * They are the company's on the server now (api/labour.ts); what is left here is only read once,
 * to offer saving it for the company (LocalFiguresOffer), and then removed.
 */
export function readLocal<T>(key: string, tenantId: number | undefined, fallback: T): T {
  try {
    const raw = localStorage.getItem(`timefly.${key}.${tenantId ?? 0}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
export function writeLocal<T>(key: string, tenantId: number | undefined, value: T): void {
  try {
    localStorage.setItem(`timefly.${key}.${tenantId ?? 0}`, JSON.stringify(value));
  } catch {
    /* private mode */
  }
}

/** Forget a browser-held value, once it is saved for the company (api/labour.ts, LocalFiguresOffer). */
export function removeLocal(key: string, tenantId: number | undefined): void {
  try {
    localStorage.removeItem(`timefly.${key}.${tenantId ?? 0}`);
  } catch {
    /* private mode */
  }
}

export const HOURS = Array.from({ length: 16 }, (_, i) => i + 8); // 08 to 23
/** need[dow][hour] = people needed; dow 0 = Monday. */
export type NeedProfile = Record<number, Record<number, number>>;
export const readNeed = (tenantId: number | undefined): NeedProfile =>
  readLocal<NeedProfile>('need', tenantId, {});
export const needAt = (need: NeedProfile, dow: number, hour: number): number => need[dow]?.[hour] ?? 0;
export type Sales = Record<string, number>;
export const readSales = (tenantId: number | undefined): Sales => readLocal<Sales>('sales', tenantId, {});
/**
 * The wage-target percentage. Guarded, because this one is arithmetic: anything that is not a
 * usable percentage would otherwise come back as itself and turn the figure beside it into
 * NaN. readLocal only catches a value that will not parse, not one that parses to the wrong
 * thing (2026-09-21).
 */
export const readTarget = (tenantId: number | undefined): number => {
  // The type is checked before the range, not coerced into it. Number(null) is 0, and 0 is a
  // valid target, so coercing would turn "nobody set this" into "aim for nought per cent".
  // The same trap as the employer rates in rules/labour.ts.
  const raw = readLocal<unknown>('wage-target', tenantId, 30);
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 && raw <= 100 ? raw : 30;
};
/**
 * Arbeidsgiveravgift, sone 1, as a multiplier. Kept so nothing that still imports it breaks;
 * rules/labour.ts is where the cost model lives now, and it knows about pension too.
 * @deprecated use ratesOf(readRates(tenantId)) and costOf()
 */
export const AGA = 1.141;

/**
 * The two percentages a company pays on top of wages. Stored per company beside the sales and
 * the wage target, which live here for the same reason: there is no column for them and
 * migrations are not run casually (designer's report, 2026-09-21).
 *
 * The cost of that choice, written down rather than hidden: these are per browser. Two managers
 * in the same company can hold different rates and see different labour figures, and a new
 * machine starts at the defaults. When the schema next moves, these belong on the tenant.
 */
export const readRates = (tenantId: number | undefined): Partial<Rates> =>
  readLocal<Partial<Rates>>('rates', tenantId, {});
